import { useState } from "react";
import { useRouter } from "next/compat/router";
import {
  Text,
  Button,
  Box,
  ErrorText,
  Heading,
  Modal,
} from "@cruk/cruk-react-components";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { fetcher } from "@fwa/src/services/apiClient";
import { fwsUrlTeamMemberPromote } from "@fwa/src/services/teamService";
import { fwsUrlMember } from "@fwa/src/services/teamMemberService";
import { fwsUrlPageParentMemberships } from "@fwa/src/services/fundraisingPageService";
import { isPast } from "@fwa/src/utils/timeUtils";
import { userNameForPage } from "@fwa/src/utils/pageUtils";

import { MemberFundraisingPage } from "@fwa/src/components/MemberFundraisingPage";
import { FormActions } from "@fwa/src/components/FormActions";

import styles from "@fwa/src/components/MemberFundraisingPageEdit/styles.module.css";

import {
  type FwsResponseData,
  type TeamMemberType,
  type FundraisingPageType,
} from "@fwa/src/types";

export function MemberFundraisingPageEdit({
  fundraisingPage,
  isLeader = false,
  teamId,
}: {
  fundraisingPage: FundraisingPageType;
  isLeader?: boolean;
  teamId: string;
}) {
  const { trackError } = useTracking();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSelectNewLeaderModal, setShowSelectNewLeaderModal] =
    useState(false);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const router = useRouter();
  const name = userNameForPage(fundraisingPage);
  const isPageClosed =
    !!fundraisingPage.closeDate && isPast(new Date(fundraisingPage.closeDate));

  const truncate = (text: string, limit = 20) =>
    text.length > limit ? `${text.substring(0, limit)}...` : text;

  const closeSelectNewLeaderModal = () => {
    setErrorMessage("");
    setIsLoading(false);
    setShowSelectNewLeaderModal(false);
  };

  const openSelectNewLeaderModal = () => {
    setShowSelectNewLeaderModal(true);
  };

  const closeRemoveMemberModal = () => {
    setErrorMessage("");
    setIsLoading(false);
    setShowRemoveMemberModal(false);
  };

  const openRemoveMemberModal = () => {
    setShowRemoveMemberModal(true);
  };

  const getFundraisingPageMembershipForTeam = async (
    pageId: string,
  ): Promise<TeamMemberType | void> => {
    const fundraisingPageMembershipForTeam: TeamMemberType | void =
      await fetcher(
        fwsUrlPageParentMemberships({
          pageId,
        }),
      )
        .then((res) => res as FwsResponseData<TeamMemberType>)
        .then((res) => res.results)
        .then((memberships) => {
          const membershipToPromote: TeamMemberType | undefined =
            memberships.find(
              (teamMember: TeamMemberType) =>
                teamMember.parentPage?.uniqueId === teamId,
            );
          return membershipToPromote;
        })
        .catch((err) => {
          trackError(err as Error, {
            component: "MemberFundraisingPageEdit",
          });
          setIsLoading(false);
          setErrorMessage("unable find page membership");
        });
    return fundraisingPageMembershipForTeam;
  };

  const handlePromoteClick = () => {
    doPromote().catch((err) => {
      trackError(err as Error, {
        component: "MemberFundraisingPageEdit",
      });
    });
  };

  const handleRemoveClick = () => {
    doRemove().catch((err) => {
      trackError(err as Error, {
        component: "MemberFundraisingPageEdit",
      });
    });
  };

  const doPromote = async () => {
    setErrorMessage("");
    setIsLoading(true);

    if (!fundraisingPage) {
      setIsLoading(false);
      setErrorMessage("unable find page");
      return undefined;
    }

    // get membeships to promote
    const fundraisingPageMembershipForTeam: TeamMemberType | void =
      await getFundraisingPageMembershipForTeam(fundraisingPage?.uniqueId);

    if (!fundraisingPageMembershipForTeam) {
      setIsLoading(false);
      setErrorMessage("unable find page membership");
      return undefined;
    }

    // promote that page membership to be team leader
    await fetcher(
      fwsUrlTeamMemberPromote({
        memberId: fundraisingPageMembershipForTeam?.uniqueId || "",
      }),
      {
        method: "POST",
      },
    )
      .then(() => {
        setIsLoading(false);
        setShowSelectNewLeaderModal(false);
        return undefined;
      })
      // this assumes you are no longer the leader re-direct from edit members to members page
      .then(() => {
        if (!router) return undefined;
        const newUrl = router.asPath.replace("/edit", "").split("?")[0];
        if (newUrl) {
          router?.push(newUrl);
        }
        return undefined;
      })
      .catch((err) => {
        trackError(err as Error, {
          component: "MemberFundraisingPageEdit",
        });
        setIsLoading(false);
        setErrorMessage("unable to promote member");
      });
    return undefined;
  };

  const doRemove = async () => {
    setErrorMessage("");
    setIsLoading(true);

    // get membeships to promote
    if (!fundraisingPage) {
      setIsLoading(false);
      setErrorMessage("unable find page");
      return undefined;
    }

    const fundraisingPageMembershipForTeam: TeamMemberType | void =
      await getFundraisingPageMembershipForTeam(fundraisingPage.uniqueId);

    if (!fundraisingPageMembershipForTeam) {
      setIsLoading(false);
      setErrorMessage("unable find page membership");
      return undefined;
    }

    // remove that page membership to be team leader
    await fetcher(
      fwsUrlMember({
        memberId: fundraisingPageMembershipForTeam?.uniqueId,
      }),
      {
        method: "PATCH",
        body: JSON.stringify({ membershipStatus: "removed" }),
      },
    )
      .then(() => {
        setIsLoading(false);
        setShowRemoveMemberModal(false);
        return undefined;
      })
      .then(() => router?.reload()) // TODO a bit brash, might be a smoother way
      .catch((err) => {
        trackError(err as Error, {
          component: "MemberFundraisingPageEdit",
        });
        setIsLoading(false);
        setErrorMessage("unable to remove member");
      });

    return undefined;
  };

  return (
    <>
      {fundraisingPage && (
        <MemberFundraisingPage
          fundraisingPage={fundraisingPage}
          isLeader={isLeader}
        >
          <Box paddingTop="xs" margin="none">
            {isLeader ? (
              <Text
                style={{ lineHeight: "3rem" }}
                textAlign="center"
                textWeight="heavy"
              >
                Leader
              </Text>
            ) : isPageClosed ? (
              <Text
                style={{ lineHeight: "3rem" }}
                textAlign="center"
                textWeight="heavy"
              >
                Page Closed
              </Text>
            ) : (
              <div className={styles["edit-button-wrapper"]}>
                <Button
                  full
                  appearance="secondary"
                  aria-label={`Choose ${name} as team leader`}
                  onClick={() => {
                    openSelectNewLeaderModal();
                  }}
                  data-cta-type="choose-leader"
                >
                  Make Leader
                </Button>

                <Button
                  full
                  aria-label={`Remove ${name} from team`}
                  onClick={() => {
                    openRemoveMemberModal();
                  }}
                  data-cta-type="remove-leader"
                >
                  Remove
                </Button>
              </div>
            )}
          </Box>
        </MemberFundraisingPage>
      )}

      {showSelectNewLeaderModal && (
        <Modal
          closeFunction={closeSelectNewLeaderModal}
          modalName="select a new leader"
          startOpen
        >
          <Box paddingTop="l">
            <Heading h2 textSize="xl">
              {`Are you sure you want to make ${name} the new team leader?`}
            </Heading>
            <Text marginBottom="l">
              {`You will no longer be able to edit this team or it's memberships.`}
            </Text>

            {!!errorMessage.length && (
              <Box>
                <ErrorText>{errorMessage}</ErrorText>
              </Box>
            )}
            <Box>
              <FormActions
                submitLabel={`Make ${truncate(name)} leader`}
                onCancel={closeSelectNewLeaderModal}
                isLoading={isLoading}
                onSubmit={handlePromoteClick}
                submitButtonType="button"
              />
            </Box>
          </Box>
        </Modal>
      )}

      {showRemoveMemberModal && (
        <Modal
          closeFunction={closeRemoveMemberModal}
          modalName="remove fundraiser confirmation"
          startOpen
        >
          <Box paddingTop="l">
            <Heading h2 textSize="xl">
              {`Are you sure you want remove ${name} from this team?`}
            </Heading>

            <Text marginBottom="l">
              They will no longer be able to join this team.
            </Text>

            {!!errorMessage.length && (
              <Box>
                <ErrorText>{errorMessage}</ErrorText>
              </Box>
            )}
            <Box>
              <FormActions
                cancelLabel="Let them stay"
                submitLabel="Remove from team"
                onCancel={closeRemoveMemberModal}
                isLoading={isLoading}
                onSubmit={handleRemoveClick}
                submitButtonType="button"
              />
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
}

export default MemberFundraisingPageEdit;
